import inRange from 'lodash/inRange';

import ApiClient from '../../../helpers/ApiClient';
import { Exercise } from '../../../helpers/fetchExternalExerciseIds';
import { getCampusApiClient } from '../../../helpers/getClients';
import { REQUEST_STATUS } from '../../selectors';

const LEARN_RECAP_SUPPORTED_COURSES = [
  58, // Introduction to R
  672, // Intermediate R
  735, // Introduction to Python
  799, // Intermediate Python
  4914, // Introduction to the Tidyverse
  13367, // Data Manipulation in SQL
  20822, // Introduction to Data Visualization with ggplot2
  22066, // Data Manipulation with pandas
  28314, // Supervised Learning with scikit-learn
  29302, // Introduction to SQL
  29303, // Intermediate SQL
  29304, // Joining Data in SQL
];

export type LearnRecap = {
  lastActiveDate: Date;
  learningRecap: string;
  lessons: Array<{
    chapterNumber: number;
    id: number;
    number: number;
    title?: string;
  }>;
};

export default async ({
  courseAsPromise,
  exerciseNumber,
  exercisesAsPromise,
  isPreview,
  preFetchedData,
}: {
  courseAsPromise: Promise<{ id: number }>;
  exerciseNumber: number;
  exercisesAsPromise: Promise<Exercise[]>;
  isPreview: boolean;
  preFetchedData: any;
}): Promise<LearnRecap | null> => {
  const preFetchedDataRequest = preFetchedData.get('learnRecap');
  switch (preFetchedDataRequest.get('status')) {
    case REQUEST_STATUS.SUCCESS:
      return Promise.resolve(preFetchedDataRequest.get('data'));
    case REQUEST_STATUS.FAILED:
      return Promise.reject(preFetchedDataRequest.get('data'));
    default: {
      const course = await courseAsPromise;
      const client = getCampusApiClient();

      if (isPreview || !LEARN_RECAP_SUPPORTED_COURSES.includes(course.id)) {
        return Promise.resolve(null);
      }

      const exercises = await exercisesAsPromise;
      let nextExerciseNumber: number = exerciseNumber;
      if (!inRange(exerciseNumber - 1, exercises.length)) {
        nextExerciseNumber = 1;
      }

      const nextExerciseId = exercises.find(
        (ex) => ex.number === nextExerciseNumber,
      )?.id;

      if (nextExerciseId === undefined) {
        return Promise.resolve(null);
      }

      return client
        .execute(
          ApiClient.endpoint.getCourseLearnRecap({
            courseId: course.id,
            nextExerciseId,
          }),
        )
        .toPromise()
        .then(({ body: learnRecap }: { body: LearnRecap }) => ({
          ...learnRecap,
          lastActiveDate: new Date(learnRecap.lastActiveDate),
        }))
        .catch(() => Promise.resolve(null));
    }
  }
};
